import React, {
  CSSProperties,
  StateUpdater,
  useContext,
  useState,
} from 'react';

import Iframe from '../components/Iframe';
import Offer from '../components/Offer';
import { Content } from '../components/Content';
import useSessionStorage from '../utils/useSessionStorage';
import handleConfirmation from '../utils/adaHandler';
import { Offer as TOffer, ScreenType, Settings } from '../domain';
import { visibleOffers } from '../core/products/products';
import { PopupHeader } from '../components/popup/PopupHeader';
import '../styles/iframe.css';
import { getAddedOffers } from '../core/products';
import classNames from 'classnames';
import { DiscountScreen } from '../components/discounts/DiscountScreen';
import { DiscountLink } from '../components/discounts/DiscountLink';
import isMobile from '../common/utils/isMobile';
import ModalSheetMobile from '../components/modal-sheet/ModalSheetMobile';
import PopupWrapper from '../components/popup/PopupWrapper';
import { addedOffersCardFilter, getIframeStyles } from './utils';
import AnimationContext from '../contexts/AnimationContext';

interface AppBodyProps {
  settings: Settings;
  offers: TOffer[];
  updateCartCallback: (discount: string) => void;
  currentScreen: string;
  open: boolean;
  toggle: () => void;
  setCurrentScreen: StateUpdater<ScreenType>;
  CustomCss: React.FunctionComponent;
  style: CSSProperties;
}

export default function AppBody({
  settings,
  offers,
  updateCartCallback,
  currentScreen,
  toggle,
  open,
  setCurrentScreen,
  CustomCss,
  style,
}: AppBodyProps) {
  // Set to 0 until we get the correct height
  const [contentHeight, setContentHeight] = useState(0);

  const [discountCode, setDiscountCode] = useSessionStorage(
    'giftbox-discount',
    '',
  );

  const handleOnDiscountCodeChanged = async (discountCode) => {
    setDiscountCode(discountCode);
    updateCartCallback && updateCartCallback(discountCode);
  };

  // For offers with no title and subtitle
  const determineMinHeightDiscount = () =>
    currentScreen === 'discount' && !settings.title && !settings.subtitle;

  const Wrapper = isMobile() ? ModalSheetMobile : PopupWrapper;
  const addedOffers = getAddedOffers();
  const addedVisibleOffers = visibleOffers(offers, settings).filter((offer) =>
    addedOffersCardFilter(addedOffers, offer),
  );

  const notAddedVisibleOffers = visibleOffers(offers, settings).filter(
    (offer) => !addedOffersCardFilter(addedOffers, offer),
  );

  const animationContext = useContext(AnimationContext);

  return (
    <Wrapper open={open} toggle={toggle}>
      <Iframe
        isMobile={isMobile()}
        data-cy="popup"
        className={classNames(
          `${
            addedVisibleOffers.length > 0 ? 'has-gift' : ''
          } Giftbox-Popup-Iframe-3082023`,
          {
            'opacity-0': animationContext.isAnimationInState(
              'first_visit_auto_open_desktop',
              'before',
            ),
            'opacity-50': animationContext.isAnimationInState(
              'first_visit_auto_open_desktop',
              'after',
            ),
            'opacity-100': animationContext.isAnimationInState(
              'first_visit_auto_open_desktop',
              null,
            ),
          },
        )}
        tabIndex={1}
        style={{
          ...getIframeStyles({
            contentHeight,
            discountMinHeight: determineMinHeightDiscount() ? '240px' : null,
            settings,
          }),
          ...style,
        }}
      >
        {currentScreen === ScreenType.OFFERS && (
          <Content
            setContentHeight={setContentHeight}
            contentHeight={contentHeight}
            isDiscountEnabled={settings.discounts_enabled}
          >
            <div className="min-h-full grid grid-rows-[1fr,auto]">
              <div className="overflow-x-hidden">
                <PopupHeader
                  title={settings.title}
                  subtitle={settings.subtitle}
                  onClose={toggle}
                  onKeyPress={handleConfirmation(toggle)}
                />

                <div>
                  <div
                    className={classNames(
                      'GiftBox-Added-Offers-Content grid grid-cols-1 gap-y-4 border-2 rounded-2xl p-5 border-primary-500 divide-y divide-gray-200',
                      {
                        'mb-4': notAddedVisibleOffers.length !== 0,
                        hidden: addedVisibleOffers.length === 0,
                        'opacity-0': animationContext.isAnimationInState(
                          'product_choice_added',
                          'before',
                        ),
                        'opacity-50': animationContext.isAnimationInState(
                          'product_choice_added',
                          'after',
                        ),
                        'opacity-100': animationContext.isAnimationInState(
                          'product_choice_added',
                          null,
                        ),
                      },
                    )}
                  >
                    <div>
                      <h2 className="GiftBox-Title-Condition-Met text-heading-md Giftbox-TextOverflowEllipsis Cropped-Text">
                        {settings.title_condition_met}
                      </h2>
                      <h3 className="GiftBox-Subtitle-Condition-Met text-gray-600 Giftbox-TextOverflowEllipsis Cropped-Text">
                        {settings.subtitle_condition_met}
                      </h3>
                    </div>
                    {addedVisibleOffers.map((offer) => (
                      <div key={offer.id} className="pt-4">
                        <Offer
                          offer={offer}
                          withoutCard={true}
                          settings={settings}
                        />
                      </div>
                    ))}
                  </div>

                  <div className="Giftbox-Available-Offers flex flex-col gap-4">
                    <div
                      className={classNames('flex flex-col gap-4', {
                        hidden: notAddedVisibleOffers.length === 0,
                      })}
                    >
                      {notAddedVisibleOffers.map((offer) => (
                        <Offer
                          key={offer.id}
                          offer={offer}
                          withoutCard={false}
                          settings={settings}
                        />
                      ))}
                    </div>
                  </div>
                </div>
                {settings.discounts_enabled && !settings.use_ghost_products && (
                  <DiscountLink
                    onClick={() => setCurrentScreen(ScreenType.DISCOUNT)}
                    label={settings?.discount_prompt ?? ''}
                  />
                )}
              </div>
            </div>
            <CustomCss />
          </Content>
        )}
        {currentScreen === ScreenType.DISCOUNT && (
          <DiscountScreen
            discountCode={discountCode}
            onDiscountCodeChanged={handleOnDiscountCodeChanged}
            setCurrentScreen={setCurrentScreen}
            settings={settings}
            onToggle={toggle}
          />
        )}
      </Iframe>
    </Wrapper>
  );
}
